import { Box, Button, ButtonGroup, ButtonProps } from "@mui/material";
import { Image, AttachFile, MenuBook, Block } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

export type FleetColumnToggleId = "picturePaths" | "attachments" | "logbook" | "blockedUsers";

type Props = {
  visible: Record<FleetColumnToggleId, boolean>;
  onToggle: (id: FleetColumnToggleId) => void;
  isEditable: boolean;
};

export function FleetManagementTableColumnToggles({
  visible,
  onToggle,
  isEditable
}: Readonly<Props>) {
  const { t } = useTranslation();

  if (!isEditable) return null;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
      <ButtonGroup size="small" variant="outlined" sx={{ flexWrap: "wrap" }}>
        <ColumnToggleButton
          data-testid="fleet-toggle-photos"
          onClick={() => onToggle("picturePaths")}
          visible={visible.picturePaths}
          buttonLabel={t("Image")}
          startIcon={<Image />}
        />

        <ColumnToggleButton
          data-testid="fleet-toggle-attachments"
          onClick={() => onToggle("attachments")}
          visible={visible.attachments}
          buttonLabel={t("Attachments")}
          startIcon={<AttachFile />}
        />

        <ColumnToggleButton
          data-testid="fleet-toggle-logbook"
          onClick={() => onToggle("logbook")}
          visible={visible.logbook}
          startIcon={<MenuBook />}
          buttonLabel={t("Logbook")}
        />

        <ColumnToggleButton
          data-testid="fleet-toggle-blocked-users"
          onClick={() => onToggle("blockedUsers")}
          visible={visible.blockedUsers}
          startIcon={<Block />}
          buttonLabel={t("Blocked Users")}
        />
      </ButtonGroup>
    </Box>
  );
}

type ColumnToggleButtonProps = ButtonProps & {
  visible: boolean;
  startIcon: React.ReactNode;
  buttonLabel: string;
  onClick: () => void;
};

export const ColumnToggleButton = (props: ColumnToggleButtonProps) => {
  const { visible, onClick, startIcon, buttonLabel, sx, ...rest } = props;
  const color = visible ? "primary" : "inherit";
  const variant = visible ? "contained" : "outlined";

  return (
    <Button
      {...rest}
      size="small"
      sx={{ flexWrap: "wrap", ...sx }}
      startIcon={startIcon}
      onClick={onClick}
      color={color}
      variant={variant}
    >
      {buttonLabel}
    </Button>
  );
};
